import { Action, createAction, props } from '@ngrx/store';
import { Section } from '../models';
import { LocationCardData } from '../../models/location-card.model';
import { LocationDetails } from 'app/shared/models/location-details';
import { LocationCardMonitorSeries } from 'app/shared/models/monitorsetting';
import { MonitorConfiguration } from '../reducers';
import { ValidationRule } from '../models/validator-models';

export enum FormActionType {
    // Field operations
    FIELD_SET_VALUE = '@form/field/SET_VALUE',
    FIELD_ENABLE = '@form/field/ENABLE',
    FIELD_DISABLE = '@form/field/DISABLE',
    FIELD_UPDATE_VALUE_BY_PATH = '@form/field/UPDATE_VALUE_BY_PATH',
    FIELD_SET_VALIDATORS = '@form/field/SET_VALIDATORS',
    FIELD_CLEAR_VALIDATORS = '@form/field/CLEAR_VALIDATORS',

    // Field validations
    VALIDATE_SAMPLE_RATES = '@form/VALIDATE_SAMPLE_RATES',
    STATUS_CHANGE = '@form/STATUS_CHANGE',
    PIPE_HEIGHT_WIDTH_CHANGE = '@form/PIPE_HEIGHT_CHANGE',
    CLEAR_AUTOCOMPLETE = '@form/CLEAR_AUTOCOMPLETE',
    ELEVATION_ALARM_CONVERSION = '@form/ELEVATION_ALARM_CONVERSION',
    EVALUATE_SCAN_RATE_LOCK = '@form/EVALUATE_SCAN_RATE_LOCK',

    // Section operations
    SET_GLOGAL_CONFIG = '@form/section/SET_GLOBAL_CONFIG',
    SECTION_ATTACH_TO_ROW = '@form/section/ATTACH_TO_FIELD',
    SECTION_REMOVE = '@form/section/REMOVE',

    // Form validation
    SET_FORM_VALIDITY = '@form/SET_FORM_VALIDITY',
    HANDLE_CONFIRMATION_RESULT = '@form/HANDLE_CONFIRMATION_RESULT',
    CLEAR_CONFIRMATION = '@form/CLEAR_CONFIRMATION',

    // Array section operations
    ARRAY_SECTION_ADD_ITEM = '@form/field/ARRAY_SECTION_ADD_ITEM',
    ARRAY_SECTION_SET_ITEMS = '@form/field/ARRAY_SECTION_SET_ITEMS',
    ARRAY_SECTION_REMOVE_ITEM = '@form/field/ARRAY_SECTION_REMOVE_ITEM',

    // Form lifecycle
    FORM_RESET = '@form/RESET',
    FORM_VALIDATE = '@form/VALIDATE',
    SET_FORM_DATA = '@form/SET_FORM_DATA',
    INIT_FORM = '@form/INIT_FORM',
    POPULATE_FORM = '@form/POPULATE_FORM',
    MARK_DIRTY = '@form/MARK_FORM_DIRTY'
}

// Field Actions
export interface SetFieldValuePayload<T> {
    fieldId: string;
    value: T;
}

export type FieldValue = string | number | boolean | Date | null | undefined;

export const setFieldValueAction = createAction(
    FormActionType.FIELD_SET_VALUE, 
    props<{ fieldId: string; value: FieldValue }>()
);

export function setFieldValue<T extends FieldValue>(args: SetFieldValuePayload<T>) {
    return setFieldValueAction(args);
}

export const enableField = createAction(FormActionType.FIELD_ENABLE, props<{ fieldId: string }>());

export const disableField = createAction(FormActionType.FIELD_DISABLE, props<{ fieldId: string }>());

export const setFieldValidators = createAction(
    FormActionType.FIELD_SET_VALIDATORS,
    props<{ fieldId: string; validators: ValidationRule[] }>(),
);

export const clearFieldValidators = createAction(
    FormActionType.FIELD_CLEAR_VALIDATORS,
    props<{ fieldId: string }>(),
);

export const alarmToggleActions = (enabled: boolean, fieldId: string, validators: ValidationRule[]): Action[] => [
    enabled ? enableField({ fieldId }) : disableField({ fieldId }),
    enabled ? setFieldValidators({ fieldId, validators }) : clearFieldValidators({ fieldId }),
];

export const updateFieldValueByPath = createAction(
    FormActionType.FIELD_UPDATE_VALUE_BY_PATH,
    props<{ path: string; value: string | number | boolean | Date }>(),
);


export const statusChange = createAction(FormActionType.STATUS_CHANGE, props<{ isMp1: boolean }>());
export const pipeHeightWidthChange = createAction(
    FormActionType.PIPE_HEIGHT_WIDTH_CHANGE,
    props<{ isHeight: boolean; isMp1: boolean }>(),
);
export const clearAutocomplete = createAction(FormActionType.CLEAR_AUTOCOMPLETE, props<{ fieldId: string }>());
export const evaluateScanRateLock = createAction(FormActionType.EVALUATE_SCAN_RATE_LOCK);

export interface ElevationAlarmConversionPayload {
    newInstallationSeries: number;
    installationTypeFieldId: string;
    alarmFieldIds: {
        lowLevelThreshold: string;
        highLevelThreshold: string;
        highHighThreshold: string;
    };
    isMetric: boolean;
    elevationUnit: string;
    depthUnit: string;
}

export const elevationAlarmConversion = createAction(
    FormActionType.ELEVATION_ALARM_CONVERSION,
    props<ElevationAlarmConversionPayload>(),
);

// Section Actions
export const setGlobalConfig = createAction(
    FormActionType.SET_GLOGAL_CONFIG,
    props<{
        monitorSeriesName: string;
    }>(),
);

export const setFormValidity = createAction(FormActionType.SET_FORM_VALIDITY, props<{ valid: boolean }>());

export const handleConfirmationResult = createAction(
    FormActionType.HANDLE_CONFIRMATION_RESULT,
    props<{ result: boolean }>(),
);

export const clearConfirmation = createAction(FormActionType.CLEAR_CONFIRMATION);

export const attachSectionToRow = createAction(
    FormActionType.SECTION_ATTACH_TO_ROW,
    props<{
        targetSectionId: string; // ID of the section where we want to add
        rowId: string;
        fieldId: string; // ID for the new section field
        sectionConfig: Section; // The actual section configuration
    }>(),
);

export const removeSection = createAction(
    FormActionType.SECTION_REMOVE,
    props<{ sectionId: string; parentSectionId: string }>(),
);

// Array Section Actions
export const addArraySectionItem = createAction(
    FormActionType.ARRAY_SECTION_ADD_ITEM,
    props<{
        fieldId: string;
    }>(),
);

export const setArraySectionItems = createAction(
    FormActionType.ARRAY_SECTION_SET_ITEMS,
    props<{
        fieldId: string;
        newItemsCount: number;
    }>(),
);

export const removeArraySectionItem = createAction(
    FormActionType.ARRAY_SECTION_REMOVE_ITEM,
    props<{
        fieldId: string;
        index: number;
    }>(),
);

export const markFormDirty = createAction(
    FormActionType.MARK_DIRTY
)

// Form Lifecycle Actions
export const resetForm = createAction(FormActionType.FORM_RESET);

export const setFormData = createAction(FormActionType.SET_FORM_DATA, props<{ data: LocationCardData }>());

export const initForm = createAction(FormActionType.INIT_FORM);

export const populateForm = createAction(
    FormActionType.POPULATE_FORM,
    props<{ details: LocationDetails[]; config: MonitorConfiguration | null; series: LocationCardMonitorSeries[] }>(),
);
